import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/pop-ui/pop-toast";
import { useORPC } from "@/providers/orpc-provider";
import { CodeInput, type CodeInputRef } from "../components/code-input";
import { Button, Input } from "../components/form-items";
import { type UserFormErrors, validateEmail } from "../utils";

// Helper function to extract error message
const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "message" in error) {
    return String((error as {
      message?: unknown;
    }).message);
  }
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as {
      data?: unknown;
    }).data;
    if (data && typeof data === "object" && "message" in data) {
      return String((data as {
        message?: unknown;
      }).message);
    }
  }
  return "";
};
interface LinkEmailFormProps {
  area: string;
  onFinish?: () => void;
}

// 绑定邮箱表单
export function LinkEmailForm(t0) {
  const $ = _c(67);
  const {
    area,
    onFinish
  } = t0;
  const [emailToLink, setEmailToLink] = useState("");
  const [code, setCode] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      email: "",
      code: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [errors, setErrors] = useState(t1);
  const codeInputRef = useRef(null);
  const orpc = useORPC();
  const {
    t
  } = useTranslation();
  let t2;
  if ($[1] !== orpc.user.changeEmailOtp) {
    t2 = async email => orpc.user.changeEmailOtp.call({
      email
    }, {
      context: {
        headers: {}
      }
    });
    $[1] = orpc.user.changeEmailOtp;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] !== t) {
    t3 = () => {
      setShowCodeInput(true);
      setTimeout(() => {
        codeInputRef.current?.startCountdown();
      }, 500);
      toast.success(t("toast.success.verificationCodeSentToYourNewEmail"));
    };
    t4 = error => {
      console.error("Send verification code failed:", error);
      const errorMessage = getErrorMessage(error) || t("toast.error.sendCodeFailedTryLater");
      toast.error(errorMessage);
    };
    $[3] = t;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    mutate: sendVerificationCode,
    isPending: isSendingCode
  } = useMutation(t5);
  let t6;
  if ($[10] !== orpc.user.bindEmail) {
    t6 = orpc.user.bindEmail.mutationOptions();
    $[10] = orpc.user.bindEmail;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onFinish || $[13] !== t) {
    t7 = () => {
      toast.success(t("toast.success.emailLinkedSuccessfully"));
      if (onFinish) {
        onFinish();
      }
    };
    $[12] = onFinish;
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = error_0 => {
      console.error("Link email failed:", error_0);
      const errorMessage_0 = getErrorMessage(error_0) || t("toast.error.linkEmailFailedTryLater");
      toast.error(errorMessage_0);
    };
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = {
      ...t6,
      onSuccess: t7,
      onError: t8
    };
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const {
    mutate: linkEmail,
    isPending: isLinking
  } = useMutation(t9);
  let t10;
  if ($[21] !== emailToLink || $[22] !== sendVerificationCode || $[23] !== t) {
    t10 = async () => {
      setErrors({
        email: "",
        code: ""
      });
      if (!validateEmail(emailToLink)) {
        setErrors(prev => ({
          ...prev,
          email: t("error.invalidEmailAddress")
        }));
        return;
      }
      sendVerificationCode(emailToLink);
    };
    $[21] = emailToLink;
    $[22] = sendVerificationCode;
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleResendCode = t10;
  let t11;
  if ($[25] !== emailToLink || $[26] !== sendVerificationCode || $[27] !== t) {
    t11 = async e => {
      e.preventDefault();
      setErrors({
        email: "",
        code: ""
      });
      if (!validateEmail(emailToLink)) {
        setErrors(prev_0 => ({
          ...prev_0,
          email: t("error.invalidEmailAddress")
        }));
        return;
      }
      sendVerificationCode(emailToLink);
    };
    $[25] = emailToLink;
    $[26] = sendVerificationCode;
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const handleSendCode = t11;
  let t12;
  if ($[29] !== code || $[30] !== emailToLink || $[31] !== linkEmail || $[32] !== t) {
    t12 = e_0 => {
      e_0.preventDefault();
      setErrors({
        email: "",
        code: ""
      });
      if (!code) {
        setErrors(prev_1 => ({
          ...prev_1,
          code: t("error.enterVerificationCode")
        }));
        return;
      } else {
        if (code.length !== 6) {
          setErrors(prev_2 => ({
            ...prev_2,
            code: t("error.enter6DigitVerificationCode")
          }));
          return;
        }
      }
      linkEmail({
        email: emailToLink,
        otp: code
      });
    };
    $[29] = code;
    $[30] = emailToLink;
    $[31] = linkEmail;
    $[32] = t;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const handleSubmitLink = t12;
  const isLoading = isSendingCode || isLinking;
  const t13 = showCodeInput ? handleSubmitLink : handleSendCode;
  const t14 = `emailToLink-${area}`;
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e_1 => setEmailToLink(e_1.target.value);
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const t16 = isLoading || showCodeInput;
  let t17;
  if ($[35] !== t) {
    t17 = t("placeholder.enterEmailToLink");
    $[35] = t;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const t18 = showCodeInput ? "bg-gray-100" : "";
  let t19;
  if ($[37] !== emailToLink || $[38] !== t14 || $[39] !== t16 || $[40] !== t17 || $[41] !== t18) {
    t19 = <Input id={t14} type="email" value={emailToLink} onChange={t15} disabled={t16} placeholder={t17} className={t18} />;
    $[37] = emailToLink;
    $[38] = t14;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== errors.email) {
    t20 = errors.email && <p className="text-destructive text-sm leading-[1.5]">{errors.email}</p>;
    $[43] = errors.email;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t19 || $[46] !== t20) {
    t21 = <div className="space-y-2">{t19}{t20}</div>;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== code || $[49] !== errors.code || $[50] !== handleResendCode || $[51] !== isLoading || $[52] !== showCodeInput) {
    t22 = showCodeInput && <CodeInput ref={codeInputRef} value={code} onChange={setCode} onResend={handleResendCode} error={errors.code} disabled={isLoading} autoStartCountdown={false} />;
    $[48] = code;
    $[49] = errors.code;
    $[50] = handleResendCode;
    $[51] = isLoading;
    $[52] = showCodeInput;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== isLinking || $[55] !== isSendingCode || $[56] !== showCodeInput || $[57] !== t) {
    t23 = isSendingCode ? t("button.sending") : isLinking ? t("button.linking") : showCodeInput ? t("button.confirmLink") : t("button.sendCode");
    $[54] = isLinking;
    $[55] = isSendingCode;
    $[56] = showCodeInput;
    $[57] = t;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== isLoading || $[60] !== t23) {
    t24 = <Button type="submit" className="w-full" size="lg" loading={isLoading}>{t23}</Button>;
    $[59] = isLoading;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t13 || $[63] !== t21 || $[64] !== t22 || $[65] !== t24) {
    t25 = <form onSubmit={t13} className="space-y-3">{t21}{t22}{t24}</form>;
    $[62] = t13;
    $[63] = t21;
    $[64] = t22;
    $[65] = t24;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  return t25;
}